// The log of what the bar refused, and the arithmetic that scores it.
//
// Every screen in this app is about what the engine PRINTED. That is half a
// record: a gate that refuses ninety legs a day is making ninety decisions, and
// until now not one of them was written down. A bar nobody can audit is a bar
// that only ever looks right, because the legs it turned down never came back
// to argue.
//
// So every refusal is kept with the price it would have taken, settled the next
// morning like any other leg, and given a closing-line value. Then Insights can
// print the one number that says whether the bar is set correctly:
//
//     What the bar refused: +0.3% ±0.5% over 140 legs
//
// A figure ABOVE zero with the whole interval above it means the refused legs
// were beating the close — the bar cost us — and that is a finding worth acting
// on. Everything else prints carbon and claims nothing, which is almost always
// the honest answer.
//
// IT COSTS NOTHING TO SCORE. A rejected leg needs no stake, no ticket and no
// bet: it needs the price it would have taken and the closing price of the same
// side, and the settle pass is already buying that for the legs that printed.
//
// AND IT IS CAPPED. A quiet Saturday can refuse several hundred legs across
// nine strategies and fourteen books; fifty a day is enough to measure a bar
// with and small enough that the trail stays a trail rather than a log file.
// The cap keeps the refusals a reader would care about most: the ones nearest
// to clearing.
//
// Pure: no store, no clock, no DOM. `stores/settlement.js` settles these,
// `lib/insights.js` prints the row.

import { REFUSAL_REASONS } from './engine/strategy.js';
import { canonicalStrategyId } from './engine/strategies/aliases.js';

/** How many refusals a day is worth keeping. See the note above. */
export const REJECTIONS_PER_DAY = 50;

/** What each reason is called where a person reads it. */
export const REASON_WORDS = {
  unproven: 'the model has not proved itself here',
  ev: 'the price did not pay enough over what the leg is worth',
  edge: 'the claim was too close to the market\'s own number',
  certainty: 'the three devigs disagreed about the edge',
  budget: 'the day\'s exposure was already spent',
};

/** The reasons this file will file, read off the engine rather than restated. */
export { REFUSAL_REASONS };

/**
 * The row a refused leg is stored as.
 *
 * SHAPED LIKE A PROPOSAL ON PURPOSE — a `multi` header and a `legs` array — so
 * `settleProposal` can settle it without knowing what it is, and so the leg
 * picks up a result, a closing price and a CLV through exactly the code every
 * other leg goes through. `stake` is zero and `status` is `rejected`: this was
 * never a bet, and nothing that counts money may count it.
 */
export function rejectionRow(strategy, leg, { date, pricesFrom = null, at = null }) {
  const strategyId = canonicalStrategyId(strategy?.id) ?? strategy?.id ?? null;
  const id = `${date}:rejected:${strategyId}:${leg.key}:${leg.reason}`;
  return {
    id,
    date,
    status: 'rejected',
    reason: leg.reason,
    strategyId,
    strategyName: strategy?.name ?? strategyId,
    multi: {
      id,
      status: 'rejected',
      forDate: date,
      strategyId,
      label: strategy?.name ?? strategyId,
      stake: 0,
      combinedOdds: leg.price,
      bookmaker: leg.bookmakerName ?? leg.bookmaker,
      bookmakerKey: leg.bookmaker,
    },
    legs: [{
      id: `${id}-leg`,
      multiId: id,
      eventId: leg.providerEventId ?? leg.matchKey,
      providerEventId: leg.providerEventId ?? null,
      matchKey: leg.matchKey,
      side: leg.side,
      selection: leg.selection,
      match: leg.match,
      sport: leg.sport,
      sportTitle: leg.sportTitle ?? null,
      commenceTime: leg.kickoff,
      price: leg.price,
      bookmakerKey: leg.bookmaker,
    }],
    pricesFrom,
    at,
  };
}

/**
 * How near a refusal came to clearing, as the sort key the cap is applied on.
 *
 * NOT random and not first-come. The fifty worth keeping are the fifty that
 * nearly printed: a leg refused by a hair is evidence about where the bar sits,
 * and one refused by a mile is evidence about the board. `unproven` is the
 * exception in the other direction — nothing about the leg was measured at all,
 * so it sorts last and only fills whatever room is left.
 */
const nearness = (row) => (row?.reason === 'unproven' ? -1 : 0);

/**
 * The day's refusals, from one pad's worth of strategy results.
 *
 * `results` is `[{ strategy, refused }]` — what `runStrategy` hands back, which
 * is what `padResults` holds. Deduplicated on the row id, so a strategy asked
 * twice in one day does not file the same refusal twice, and capped.
 */
export function rejectionsFrom(results, { date, pricesFrom = null, at = null, max = REJECTIONS_PER_DAY } = {}) {
  const out = [];
  const seen = new Set();
  for (const result of results ?? []) {
    for (const leg of result?.refused ?? []) {
      if (!leg?.matchKey || !Number.isFinite(leg.price)) continue;
      const row = rejectionRow(result.strategy, leg, { date, pricesFrom, at });
      if (seen.has(row.id)) continue;
      seen.add(row.id);
      out.push(row);
    }
  }
  // Nearest-to-clearing first, then by id, so one board files one list however
  // often the pad is re-read.
  out.sort((a, b) => nearness(b) - nearness(a) || a.id.localeCompare(b.id));
  return out.slice(0, max);
}

/**
 * What the refused legs were worth against the close: the same aggregate shape
 * `insights.js` reads for a strategy, so the row it prints and the rows beside
 * it are counted the same way.
 *
 * Only legs with a `clvSource` count — the same rule `summariseDay` applies, for
 * the same reason (engine review #6): a leg that carries its own book's settling
 * price and was never measured against the market has the price and not the
 * answer.
 */
export function refusedAgg(settledByDate, { from = null, to = null } = {}) {
  let legsWithClose = 0;
  let clvSum = 0;
  let clvSqSum = 0;
  let legs = 0;
  const reasons = {};

  for (const [date, list] of Object.entries(settledByDate ?? {})) {
    if (from && date < from) continue;
    if (to && date > to) continue;
    for (const row of list ?? []) {
      if (row?.status !== 'rejected') continue;
      reasons[row.reason] = (reasons[row.reason] ?? 0) + 1;
      for (const leg of row.legs ?? []) {
        legs += 1;
        if (!leg?.clvSource || !Number.isFinite(leg?.clv)) continue;
        legsWithClose += 1;
        clvSum += leg.clv;
        clvSqSum += leg.clv * leg.clv;
      }
    }
  }

  return {
    legs, legsWithClose, clvSum, clvSqSum, reasons,
  };
}

/** How many legs one day's log refused, whatever came of them. */
export const refusedCount = (list) =>
  (list ?? []).filter((row) => row?.status === 'rejected').length;
